import { useState } from "react";

import { useDroppable } from "@dnd-kit/core";
import {
  faBox,
  faFolder,
  faLink,
  faPen,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { NavLink, type To } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { NO_FOLDER_FILTER } from "src/api";
import { Icon } from "src/base-components/Icon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { FlowFolderListItem } from "src/clients/flow-api";
import { Tooltip } from "src/design-system/Tooltip";
import { DND_NO_FOLDER_ID } from "src/flowsOverview/v2/FlowDndProvider";
import { useCapabilities } from "src/hooks/useCapabilities";

export type FolderVariant = "default" | "sidebar";

const variantStyles = {
  default: {
    folderItem: "px-[7px] py-1.5 rounded w-full",
    count: "text-gray-800",
    selected: "bg-gray-200",
  },
  sidebar: {
    folderItem: "px-2 py-1.5 w-[203px] h-8 rounded-lg hover:bg-gray-50",
    count: "text-gray-500",
    selected: "bg-gray-100",
  },
} as const;

type BaseProps = {
  variant?: FolderVariant;
  to: To;
  onRequestSelect: (folderId: string) => void;
  onRequestEdit?: (folder: FlowFolderListItem) => void;
  onRequestDelete: (folder: FlowFolderListItem) => void;
  onCopyLink: (folder: FlowFolderListItem | undefined) => void;
  selected: boolean;
  tooltipShouldBeClosed: boolean;
  setTooltipShouldBeClosed: (shouldBeClosed: boolean) => void;
};
export type Props = BaseProps &
  (
    | {
        folder: FlowFolderListItem;
        count?: never;
      }
    | {
        folder: undefined;
        count: number;
      }
  );

export const Folder: React.FC<Props> = ({
  variant = "default",
  to,
  folder,
  selected,
  onRequestSelect,
  onRequestEdit,
  onRequestDelete,
  onCopyLink,
  count,
  tooltipShouldBeClosed,
  setTooltipShouldBeClosed,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { flowFolders } = useCapabilities();

  const rawId = folder?.id || DND_NO_FOLDER_ID;
  const droppableId = variant === "sidebar" ? `sidebar-${rawId}` : rawId;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { folderId: folder?.id ?? null },
  });
  const styles = variantStyles[variant];

  const dropdownItems: OptionsDropdownElement[] = [];
  if (folder !== undefined && flowFolders.canEdit) {
    dropdownItems.push({
      key: "Rename",
      icon: faPen,
      action: () => {
        onRequestEdit?.(folder);
      },
    });
  }

  dropdownItems.push({
    key: "Copy link",
    icon: faLink,
    action: () => {
      onCopyLink(folder);
    },
  });

  if (folder !== undefined && flowFolders.canDelete) {
    dropdownItems.push(MENU_DIVIDER);
    dropdownItems.push({
      key: "Delete",
      icon: faTrashCan,
      action: () => {
        onRequestDelete(folder);
      },
    });
  }

  const folderItemDataLoc =
    (folder ? `folder-${folder?.name}` : "no-folder") +
    (selected ? "-selected" : "");

  const folderItemClassName = twJoin(
    "group/folder flex min-w-0 items-center border",
    styles.folderItem,
    selected && styles.selected,
    isOver ? "border-indigo-500" : "border-transparent",
  );

  const folderIcon =
    variant === "default" ? (
      <Icon
        color="text-gray-500"
        icon={folder === undefined ? faBox : faFolder}
        size="base"
      />
    ) : (
      <div className="pr-1.5">
        <Icon
          color="text-gray-500"
          icon={folder === undefined ? faBox : faFolder}
          size="xs"
        />
      </div>
    );

  let content = (
    <div
      className={folderItemClassName}
      data-loc={folderItemDataLoc}
      ref={setNodeRef}
    >
      <NavLink
        className="flex w-full min-w-0 items-center"
        to={to}
        onClick={() => onRequestSelect(folder?.id ?? NO_FOLDER_FILTER)}
      >
        {folderIcon}
        <span
          className="truncate text-left font-inter-medium-13px text-gray-800"
          data-loc="folder-name"
        >
          {folder === undefined ? "No folder" : folder.name}
          &nbsp;
        </span>
        <span
          className={twJoin(
            "grow text-left font-inter-medium-13px",
            styles.count,
          )}
          data-loc={
            folder === undefined ? "no-folder-flow-count" : "folder-flow-count"
          }
        >
          ({folder === undefined ? count : folder.flow_count})
        </span>
      </NavLink>
      <EllipsisOptionsDropdown
        buttonClassName={twJoin(
          "text-gray-800 opacity-0 group-hover/folder:opacity-100 data-[menu-open=true]:opacity-100",
          variant === "default" ? "ml-2" : "",
        )}
        dataLoc="folder-options-dropdown-button"
        elements={dropdownItems}
        iconSize={variant === "sidebar" ? "xs" : "sm"}
        placement="bottom start"
      />
    </div>
  );

  // Show a tooltip for names that are truncated (with current width this can happen after 12 characters).
  if (variant === "sidebar" && folder?.name && folder.name.length > 12) {
    content = (
      <Tooltip
        disabled={!folder?.name}
        open={!tooltipShouldBeClosed && tooltipOpen}
        onOpenChange={(open) => {
          if (open) {
            setTooltipShouldBeClosed(false);
          }
          setTooltipOpen(open);
        }}
        placement="right"
        title={folder?.name}
      >
        {content}
      </Tooltip>
    );
  }
  return <li className="w-full min-w-0">{content}</li>;
};
